/**
 * EventsListing component.
 * @module components/Events/EventsListing
 */

import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import moment from 'moment';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Container, Header, Grid, Card, Form, Loader } from 'semantic-ui-react';
import { BodyClass, hasBlocksData } from '@plone/volto/helpers';
import { UniversalLink, FormattedDate, Pagination, Image } from '@plone/volto/components';
import { searchContent, getVocabulary } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import { keys, isEmpty, map } from 'lodash';
import { useDebounce } from "use-debounce";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faClock, faCalendar } from '@fortawesome/free-regular-svg-icons';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { DateRangePicker } from 'react-dates';
import { toBackendLang } from '@plone/volto/helpers/Utils/Utils';
import { formatDate, formatTime } from '../../helpers'
import { Icon } from '../'; 

const messages = defineMessages({
  labelText: {
    id: 'By words',
    defaultMessage: 'By words',
  },
  placeholderText: {
    id: 'What are you looking for?',
    defaultMessage: 'What are you looking for?',
  },
  defaultOptionSubjects: {
    id: 'All subjects',
    defaultMessage: 'All subjects',
  },
  labelSubject: {
    id: 'By category',
    defaultMessage: 'By category',
  },
  placeholderAllSubjects: {
    id: 'All subjects',
    defaultMessage: 'All subjects',
  },
  startDate: {
    id: 'Start date',
    defaultMessage: 'Start date',
  },
  endDate: {
    id: 'End date',
    defaultMessage: 'End date',
  },
});


const EventsListing = (props) => {
  const { content } = props;
  const dispatch = useDispatch();
  const intl = useIntl();
  const lang = content.language.token;
  const [isInitialized, setIsInitialized] = useState(false);
  const [dateParam, setDateParam] = useState(null);
  const [searchableTextParam, setSearchableTextParam] = useState('');
  const events = useSelector((state) => state.search.subrequests.events?.items);
  const total = useSelector((state) => state.search.subrequests.events?.total);
  const isLoading = useSelector((state) => state.search.subrequests.events?.loading);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(12);
  const [search, setSearch] = useState(0);
  const [searchableTextInput, setSearchableText] = useState(searchableTextParam);
  const [searchableText] = useDebounce(searchableTextInput, 500);
  const [subject, setSubject] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [focused, setFocused] = useState(null);
  const customRoot = useSelector((state) => state.navroot.data?.custom_root);

  const vocabularySubjects = 'plone.app.vocabularies.Keywords';
  const subjectData = useSelector(
    (state) => state.vocabularies[vocabularySubjects]?.items ?? [],
    shallowEqual,
  );
  const subjectItems = useMemo(() => [
    { value: '', label: intl.formatMessage(messages.defaultOptionSubjects) },
    ...subjectData.map(item => ({
      value: item.label,
      label: item.label,
    })),
  ], [subjectData]);

  const getEventSubject = function (Subject) {
    if (!subjectItems) {
      return Subject
    }
    for (let item of subjectItems) {
      if (item.value === Subject) return item.label
    }
    return Subject
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setDateParam(urlParams.get('date'));
    const textParam = urlParams.get('searchableText') || '';
    setSearchableTextParam(textParam);
    setSearchableText(textParam);
    setIsInitialized(true);
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (dateParam) {
      const parsedDate = moment(dateParam, 'DD-MM-YYYY');
      setStartDate(parsedDate);
      setEndDate(parsedDate.clone().add(1, 'day'));
    }
  }, [dateParam]);

  useEffect(() => {
    dispatch(getVocabulary({ vocabNameOrURL: vocabularySubjects, lang: content.language.token }));
  }, [dispatch]);

  useEffect(() => {
    if (!isInitialized) return;
    if (startDate && !endDate) return;

    let path = content['@id']
    let contentFilter = {
      portal_type: ['Event'],
      b_size: pageSize,
      b_start: currentPage * pageSize,
      review_state: 'published',
      SearchableText: searchableText,
      sort_on: 'start',
      metadata_fields: ['start', 'end', 'open_end', 'whole_day', 'location', 'Subject', 'hasPreviewImage']
    };
    if (subject && subject !== '') {
      contentFilter.Subject = subject;
    }

    if (startDate && endDate) {
      contentFilter['start.query'] = [endDate.format('YYYY/MM/DD') + ' 23:59'];
      contentFilter['start.range'] = 'max';
      contentFilter['end.query'] = [startDate.format('YYYY/MM/DD') + ' 00:00'];
      contentFilter['end.range'] = 'min';
    } else {
      contentFilter['end.query'] = [moment().format('YYYY/MM/DD HH:mm')]
      contentFilter['end.range'] = 'min'
    }

    if (customRoot?.['@type'] === 'Minisite' && customRoot?.['UID']) {
      path = '/'
      contentFilter['related_minisites'] = customRoot['UID']
    }

    dispatch(
      searchContent(
        flattenToAppURL(path),
        contentFilter,
        'events',
      ),
    );
  }, [dispatch, currentPage, pageSize, searchableText, startDate, endDate, dateParam, subject, customRoot, search, isInitialized]);

  useEffect(() => {
    const searchForm = document.querySelector('.searchList');
    const searchFormPos = searchForm ? searchForm.offsetTop : 0;
    let scrollPos = window.scrollY;
    if (scrollPos > searchFormPos && searchForm) {
      window.scrollTo({ top: searchFormPos - 30 })
    }
  }, [currentPage]);

  useEffect(() => {
    moment.locale(lang || 'ca');
  }, [lang]);

  const onChangePage = (event, activePage) => {
    event.preventDefault();
    setCurrentPage(activePage.value);
  };

  const onChangePageSize = (event) => {
    event.preventDefault();
    setPageSize(parseInt(event.target.text));
    setCurrentPage(0);
  };

  const onChangeSearchableText = (event, data) => {
    event.preventDefault();
    setSearchableText(data.value);
    setCurrentPage(0);
  };

  const onChangeSubject = (event, data) => {
    event.preventDefault();
    setSubject(data.value);
    setCurrentPage(0);
  };

  const onDatesChange = (data) => {
    if (data.startDate && data.endDate) {
      setCurrentPage(0);
      setStartDate(data.startDate);
      setEndDate(data.endDate);
    } else {
      setCurrentPage(0);
      setStartDate(data.startDate);
      setEndDate(data.endDate);
    }
  };


  const onSubmit = (event) => {
    event.preventDefault();
    setSearch(search + 1);
    setCurrentPage(0);
  };

  const onFocusChange = (data) => {
    setFocused(data.focused)
  };

  const getEventDate = function (start, end, open_end) {
    const optionsFullTime = { year: 'numeric', month: '2-digit', day: '2-digit' };

    if (open_end) {
      return (
        <time dateTime={start} className='d-flex align-items-center'>
          <FontAwesomeIcon className="eventsItem__icon me-2" icon={faCalendar} />
          <span suppressHydrationWarning>{formatDate(start, optionsFullTime)}</span>
        </time>
      );
    }

    const startDay = formatDate(start);
    const endDay = formatDate(end);

    if (startDay === endDay) {
      return (
        <time dateTime={start} className='d-flex align-items-center'>
          <FontAwesomeIcon className="eventsItem__icon me-2" icon={faCalendar} />
          <span suppressHydrationWarning>{formatDate(start, optionsFullTime)}</span>
        </time>
      );
    }

    return (
      <>
        <time suppressHydrationWarning dateTime={start} className='d-flex align-items-center '>
            <FontAwesomeIcon className="eventsItem__icon me-2" icon={faCalendar} />
            <span suppressHydrationWarning>{formatDate(start, optionsFullTime)}</span>
          
        </time > &mdash;
        <time suppressHydrationWarning dateTime={end} className='d-flex align-items-center'>
          <span className="eventsItem__date__day"> 
            <span suppressHydrationWarning>{formatDate(end, optionsFullTime)}</span>
          </span>
        </time>
      </>
    );
  };

  return (
    <>
      <BodyClass className='eventslisting' />

      {hasBlocksData(content) ? (
        <Container>
          <Grid>
            <Grid.Column width={12}>
              <RenderBlocks {...props} />
            </Grid.Column>
          </Grid>
        </Container>
      ) : null}

      <Container className="pb-4">
        <Form className="searchList searchList--eventListing d-print-none" onSubmit={onSubmit}>
          <Header as="h2" className='justify-content-center searchList__title' size='small' textAlign='center'><FormattedMessage id="Filter" defaultMessage="Filter" /></Header>
          <Form.Group widths="equal" className="align-items-end">
            <Form.Input
              id="textInputId"
              fluid
              label={intl.formatMessage(messages.labelText)}
              focus
              name="searchableText"
              value={searchableTextInput}
              onChange={onChangeSearchableText}
              placeholder={intl.formatMessage(messages.placeholderText)}
              type="text"
            />
            <Form.Select
              id="subjectSelectId"
              fluid
              label={intl.formatMessage(messages.labelSubject)}
              options={map(subjectItems, (item, index) => {
                return {
                  key: index,
                  value: item.value,
                  text: item.label,
                };
              })}
              name="subject"
              value={subject}
              onChange={onChangeSubject}
              placeholder={intl.formatMessage(messages.placeholderAllSubjects)}
              aria-label={intl.formatMessage(messages.labelSubject)}
            />
            <div className="field searchList__date">
              <label htmlFor="startDate" className="searchList__label"><FormattedMessage id="Date range" defaultMessage="Date range" /></label>
              <div className="ui input date-input">
                <DateRangePicker
                  startDate={startDate}
                  startDateId="startDate"
                  startDatePlaceholderText={intl.formatMessage(messages.startDate)}
                  endDate={endDate}
                  endDateId="endDate"
                  onDatesChange={onDatesChange}
                  endDatePlaceholderText={intl.formatMessage(messages.endDate)}
                  numberOfMonths={1}
                  noBorder
                  showClearDates
                  displayFormat={moment
                    .localeData(toBackendLang(lang || 'ca'))
                    .longDateFormat('L')}
                  focusedInput={focused}
                  onFocusChange={(focusedInput) => setFocused(focusedInput)}
                  isOutsideRange={() => false}
                />
              </div>
            </div>
          </Form.Group>
        </Form>
      </Container>

      {isLoading ? (
        <div className="py-4"><Loader active inline="centered" size="large" content={intl.formatMessage({ id: 'Loading...', defaultMessage: 'Loading...' })} /></div>
      ) : (
        <div className="py-4">
          {!isEmpty(events) ? (
            <Container className="events eventsListing">
              <Grid>
                <Grid.Row columns={12} as="ul" className='list-unstyled eventsList my-0'>
                  {events.map((item) => (
                    <Grid.Column className="mb-4" mobile={12} tablet={6} computer={4}  key={item['@id']} as="li">
                      <Card fluid as="article" className="eventsItem position-relative">

                        {item?.hasPreviewImage &&
                          <div className="eventsItem__figure">
                            <Image
                              item={item}
                              loading='lazy'
                              alt=""
                              className="eventsItem__img m-0 image"
                              sizes="(max-width: 767px) 100vw, (max-width: 991px) 50vw, (max-width: 1599px) 33.33vw, 25vw"
                            />
                          </div>
                        }
                        <Card.Content className='eventsItem__text'>                          
                          {item.event_subjects?.length > 0 && (
                            <Card.Meta className="eventsItem__meta mb-3">
                              <ul className="list-unstyled eventsItem__meta__list mt-0 mb-2">
                                {map(item.event_subjects.slice(0, 1), (item, index) => (
                                  <li key={item}>
                                    <span className="newsItem__subject__item" >{getEventSubject(item)}</span>
                                  </li>
                                ))}
                              </ul>

                            </Card.Meta>
                          )}
                          <Card.Header className="eventsItem__title" as="h2">
                            <UniversalLink
                              className="eventsItem__link stretched-link"
                              href={flattenToAppURL(item['@id'])}>
                              {item.title}
                            </UniversalLink>
                          </Card.Header>

                          <ul className="list-unstyled mt-0">
                            <li className='mb-3'>
                              <div className="eventsItem__date d-flex gap-1 align-items-center">{getEventDate(item.start, item.end, item.open_end)}</div>
                            </li>
                            {!item.whole_day && (
                            <li className='d-flex align-items-center mb-3'>
                              <FontAwesomeIcon className='eventsItem__icon me-2 ' icon={faClock} />
                              <span suppressHydrationWarning>{formatTime(item.start)}</span>
                            </li>
                            )}
                            {item.location && (
                            <li className='mb-3 align-items-center d-flex'>
                              <Icon name='location-outline' className='eventsItem__icon me-2' /> {item.location}
                            </li>
                            )}
                          </ul>
                          <div className="text-end mt-auto">
                            <FontAwesomeIcon icon={faArrowRight} />
                          </div>
                        </Card.Content>
                      </Card>
                    </Grid.Column>
                  ))}
                </Grid.Row>
              </Grid>

              {total > pageSize && (
                <nav className="pagination">
                  <Pagination
                    current={currentPage}
                    total={Math.ceil(total / pageSize)}
                    onChangePage={onChangePage}
                    onChangePageSize={onChangePageSize}
                  />
                </nav>
              )}

            </Container>
          ) : (
            <Container className="events EventsListing">
              <p><FormattedMessage id="No events found" defaultMessage="No events found" /></p>
            </Container>
          )}
        </div>
      )}
    </>
  );
};


export default EventsListing;
